/**
 * LogoUploadPanel — v2.37 — Manage the user's branding logo.
 *
 * Uploads a PNG/JPEG/WebP (≤ 2 MB) which is stored in GridFS and used to
 * brand the title block of every template-stamped deliverable. The
 * dominant colour is extracted server-side and re-rendered as a vector
 * accent bar in the CAD output.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Image as ImageIcon, Upload, Trash, Palette,
} from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function LogoUploadPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/me/logo/info");
      setInfo(r.data);
    } catch (e) {
      toast.error(`Couldn't load logo info: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo too large — max 2 MB.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/auth/me/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Logo uploaded — accent ${r.data.color || "auto"}`);
      reload();
    } catch (err) {
      toast.error(`Upload failed: ${err?.response?.data?.detail || err?.message || err}`);
    } finally {
      setBusy(false);
      e.target.value = "";   // reset so re-upload of the same file triggers
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove your logo? Future deliverables will fall back to the org name only.")) return;
    setBusy(true);
    try {
      await api.delete("/auth/me/logo");
      toast.success("Logo removed");
      reload();
    } catch (e) {
      toast.error(`Delete failed: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#0F1218]" data-testid="logo-upload-panel">
      <div className="border-b border-white/10 p-4 bg-[#1A1F29] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ImageIcon size={18} className="text-[#00E5FF]" />
          <div>
            <div className="label-tag">// Branding · Org Logo</div>
            <div className="text-[11px] text-gray-500 font-mono mt-0.5">
              Stamped onto every template-driven deliverable&apos;s title block.
            </div>
          </div>
        </div>
      </div>
      <div className="p-5 flex items-center gap-5 flex-wrap">
        {loading ? (
          <div className="text-[12px] font-mono text-gray-500">Loading…</div>
        ) : info?.has_logo ? (
          <>
            <div className="border border-white/10 bg-white p-2"
                  data-testid="logo-preview-wrap">
              <img src={`${API_BASE}/api/auth/me/logo?t=${info.uploaded_at}`}
                    alt="Org logo"
                    style={{ maxHeight: 72, maxWidth: 200 }}
                    data-testid="logo-preview-img" />
            </div>
            <div className="font-mono text-[12px] space-y-0.5">
              <div className="text-gray-300">
                <span className="text-gray-500">Size:</span>{" "}
                <span data-testid="logo-size">{(info.size_bytes/1024).toFixed(1)} KB</span>
              </div>
              <div className="text-gray-300 flex items-center gap-1.5">
                <Palette size={11} className="text-gray-500" />
                <span className="text-gray-500">Accent:</span>{" "}
                <span data-testid="logo-color"
                       style={{ color: info.color }}>{info.color}</span>
                <span style={{ background: info.color, width: 14, height: 14, display: "inline-block", border: "1px solid #444" }} />
              </div>
              <div className="text-gray-500">
                Uploaded {info.uploaded_at?.slice(0, 10)}
              </div>
            </div>
            <div className="flex flex-col gap-2 ml-auto">
              <label className="btn-ghost text-sm inline-flex items-center gap-2 cursor-pointer disabled:opacity-50">
                <Upload size={14} />
                Replace
                <input type="file" accept="image/png,image/jpeg,image/webp"
                        onChange={onFile} className="hidden"
                        disabled={busy}
                        data-testid="logo-replace-input" />
              </label>
              <button onClick={onDelete} disabled={busy}
                      className="btn-ghost text-sm inline-flex items-center gap-2 text-red-400 hover:text-red-300 disabled:opacity-50"
                      data-testid="logo-delete-btn">
                <Trash size={14} />
                Remove
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="border border-dashed border-white/15 p-6 text-center flex-1 min-w-[300px]"
                  data-testid="logo-empty">
              <ImageIcon size={32} className="mx-auto text-gray-600 mb-2" />
              <div className="text-sm text-gray-400">No logo uploaded yet.</div>
              <div className="text-[11px] text-gray-500 font-mono mt-1">
                PNG / JPEG / WebP · max 2 MB · transparent backgrounds OK
              </div>
            </div>
            <label className="btn-primary inline-flex items-center gap-2 cursor-pointer disabled:opacity-50">
              <Upload size={14} />
              {busy ? "Uploading…" : "Upload logo"}
              <input type="file" accept="image/png,image/jpeg,image/webp"
                      onChange={onFile} className="hidden"
                      disabled={busy}
                      data-testid="logo-upload-input" />
            </label>
          </>
        )}
      </div>
    </div>
  );
}
